import { useState, useEffect, useLayoutEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { createChart, CandlestickSeries } from 'lightweight-charts';
import { TIMEFRAMES, THEMES } from './chartConstants';

const API_URL = process.env.REACT_APP_API_URL || '';

const MiniChartPane = forwardRef(function MiniChartPane(
  { initialTicker = 'MSFT', initialPeriod = '1y', theme = 'dark', onClose, onRangeChange },
  ref
) {
  const containerRef  = useRef(null);
  const chartRef      = useRef(null);
  const seriesRef     = useRef(null);
  const syncingRef    = useRef(false);

  useImperativeHandle(ref, () => ({
    setTimeRange: (range) => {
      if (!chartRef.current || !range) return;
      syncingRef.current = true;
      try { chartRef.current.timeScale().setVisibleLogicalRange(range); } catch {}
      syncingRef.current = false;
    },
  }), []);

  const [ticker,      setTicker]      = useState(initialTicker);
  const [tickerInput, setTickerInput] = useState(initialTicker);
  const [period,      setPeriod]      = useState(initialPeriod);
  const [loading,     setLoading]     = useState(false);
  const [showPeriod,  setShowPeriod]  = useState(false);
  const [legendData,  setLegendData]  = useState({});

  const colors = THEMES[theme] || THEMES.dark;

  // ── Chart initialisation (re-run on theme change) ──────────────────────────
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const chart = createChart(container, {
      width:  container.clientWidth  || 400,
      height: container.clientHeight || 300,
      layout: { background: { color: colors.bg }, textColor: colors.text, attributionLogo: false },
      grid:   { vertLines: { color: colors.gridLines }, horzLines: { color: colors.gridLines } },
      rightPriceScale: { borderColor: colors.border },
      timeScale:       { borderColor: colors.border, timeVisible: true, secondsVisible: false },
    });
    chartRef.current = chart;

    const series = chart.addSeries(CandlestickSeries, {
      upColor:      colors.up,   downColor:      colors.down,
      wickUpColor:  colors.up,   wickDownColor:  colors.down,
      borderVisible: false,
    });
    seriesRef.current = series;

    chart.subscribeCrosshairMove((param) => {
      if (param.time) {
        const d = param.seriesData.get(series);
        if (d) setLegendData({ open: d.open, high: d.high, low: d.low, close: d.close });
      }
    });

    chart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
      if (!syncingRef.current && range && onRangeChange) {
        onRangeChange(range);
      }
    });

    let roRaf;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const { width, height } = e.contentRect;
        if (width > 0 && height > 0) {
          cancelAnimationFrame(roRaf);
          roRaf = requestAnimationFrame(() => chart.applyOptions({ width, height }));
        }
      }
    });
    ro.observe(container);

    return () => { cancelAnimationFrame(roRaf); ro.disconnect(); chart.remove(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  // ── Cache helpers ───────────────────────────────────────────────────────────
  const CACHE_TTL = 15 * 60 * 1000;
  const cacheKey  = (s, p) => `chart_cache_${s}_${p}`;
  const readCache = (s, p) => {
    try {
      const raw = localStorage.getItem(cacheKey(s, p));
      if (!raw) return null;
      const { ts, data } = JSON.parse(raw);
      if (Date.now() - ts > CACHE_TTL) { localStorage.removeItem(cacheKey(s, p)); return null; }
      return data;
    } catch { return null; }
  };
  const writeCache = (s, p, data) => {
    try { localStorage.setItem(cacheKey(s, p), JSON.stringify({ ts: Date.now(), data })); } catch {}
  };

  const applyData = (formatted) => {
    if (!seriesRef.current) return;
    seriesRef.current.setData(formatted);
    chartRef.current?.timeScale().fitContent();
    if (formatted.length) setLegendData(formatted[formatted.length - 1]);
  };

  // ── Data load ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!ticker || !seriesRef.current) return;

    const periodMap = {
      '1d': '1d', '5d': '5d', '1mo': '1mo', '3m': '3mo',
      '6m': '6mo', 'ytd': 'ytd', '1y': '1y', '3y': '3y', '5y': '5y', 'max': 'max',
    };
    const apiPeriod = periodMap[period] || period;

    // Show cache instantly, no spinner
    const cached = readCache(ticker, period);
    if (cached) { applyData(cached); setLoading(false); }
    else setLoading(true);

    // Fetch fresh data in background
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    fetch(`${API_URL}/api/chart/${ticker}?period=${apiPeriod}`, { signal: controller.signal })
      .then(r => { clearTimeout(timeout); return r.json(); })
      .then(json => {
        const raw = json.data || json.historical || (Array.isArray(json) ? json : []);
        if (!Array.isArray(raw) || !raw.length) return;
        const withTime = raw.map(d => ({
          ...d,
          time: d.timestamp ? Math.floor(d.timestamp / 1000) : (d.date || d.time || '').split('T')[0],
        }));
        const tk = (t) => typeof t === 'number' ? t : Number(String(t).replace(/-/g, '')) || 0;
        withTime.sort((a, b) => tk(a.time) - tk(b.time));
        const seen = new Set();
        const deduped = withTime.filter(d => { const k = String(d.time); if (seen.has(k)) return false; seen.add(k); return true; });
        const hasOHLC = deduped[0]?.open != null && deduped[0]?.high != null;
        const formatted = hasOHLC
          ? deduped.map(d => ({ time: d.time, open: +d.open, high: +d.high, low: +d.low, close: +d.close }))
          : deduped.map((d, i, arr) => {
              const close = +(d.price ?? d.close ?? 0);
              const prev = i > 0 ? arr[i-1].close ?? arr[i-1].price ?? close : close;
              const range = close * 0.008;
              return { time: d.time, open: +(prev*(1+(Math.random()-0.5)*0.006)).toFixed(2), high: +(Math.max(prev,close)+Math.random()*range).toFixed(2), low: +(Math.min(prev,close)-Math.random()*range).toFixed(2), close: +close.toFixed(2) };
            });
        writeCache(ticker, period, formatted);
        applyData(formatted);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [ticker, period]);

  const handleGo = () => {
    const sym = tickerInput.trim().toUpperCase();
    if (sym) { setTicker(sym); setTickerInput(sym); }
  };

  const tf = TIMEFRAMES.find(t => t.value === period);

  return (
    <div className="relative flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden"
      style={{ background: colors.bg, border: `1px solid ${colors.border}` }}>

      {/* ── Top overlay bar ─────────────────────────────────────────── */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center gap-1.5 px-2 py-1"
        style={{ background: colors.panel + 'e0', borderBottom: `1px solid ${colors.border}` }}>

        {/* Ticker */}
        <span className="text-xs font-bold flex-shrink-0" style={{ color: colors.accent }}>{ticker}</span>
        <input
          value={tickerInput}
          onChange={e => setTickerInput(e.target.value.toUpperCase())}
          onKeyDown={e => e.key === 'Enter' && handleGo()}
          className="border rounded px-1 py-0.5 text-[10px] w-14 uppercase focus:outline-none flex-shrink-0"
          style={{ background: colors.bg, borderColor: colors.border, color: colors.text }}
          placeholder="SYM"
        />
        <button onClick={handleGo}
          className="bg-blue-600 text-white px-1.5 py-0.5 rounded text-[10px] flex-shrink-0 hover:bg-blue-700">
          Go
        </button>

        {/* Period picker */}
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setShowPeriod(p => !p)}
            className="px-1.5 py-0.5 rounded text-[10px] border"
            style={{ background: colors.bg, borderColor: colors.border, color: colors.accent }}>
            {tf?.label ?? period}
          </button>
          {showPeriod && (
            <div className="absolute top-full left-0 mt-0.5 z-50 rounded shadow-xl py-1 min-w-[80px]"
              style={{ background: colors.panel, border: `1px solid ${colors.border}` }}
              onClick={e => e.stopPropagation()}>
              {TIMEFRAMES.map(t => (
                <button key={t.value} onClick={() => { setPeriod(t.value); setShowPeriod(false); }}
                  className="w-full text-left px-3 py-1 text-[10px] hover:opacity-80"
                  style={{
                    background: period === t.value ? colors.accent + '20' : 'transparent',
                    color:      period === t.value ? colors.accent : colors.text,
                  }}>
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* OHLCV legend */}
        {legendData.close && (
          <div className="flex items-center gap-1 text-[10px] flex-shrink-0">
            <span style={{ color: colors.textMuted }}>O</span>
            <span style={{ color: colors.text }}>{legendData.open?.toFixed(2)}</span>
            <span style={{ color: colors.textMuted }}>H</span>
            <span style={{ color: colors.text }}>{legendData.high?.toFixed(2)}</span>
            <span style={{ color: colors.textMuted }}>L</span>
            <span style={{ color: colors.text }}>{legendData.low?.toFixed(2)}</span>
            <span style={{ color: colors.textMuted }}>C</span>
            <span style={{ color: legendData.close >= legendData.open ? colors.up : colors.down }}>
              {legendData.close?.toFixed(2)}
            </span>
          </div>
        )}

        {/* Close pane */}
        {onClose && (
          <button onClick={onClose} className="ml-auto text-[10px] px-1 rounded hover:opacity-70"
            style={{ color: colors.textMuted }}>✕</button>
        )}
      </div>

      {/* ── Loading overlay ─────────────────────────────────────────── */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center z-20"
          style={{ background: colors.bg + 'bb' }}>
          <span className="text-xs" style={{ color: colors.textMuted }}>Loading {ticker}…</span>
        </div>
      )}

      {/* ── Chart canvas (fills full pane, overlay floats above) ── */}
      <div ref={containerRef} className="absolute inset-0" />
    </div>
  );
});

export default MiniChartPane;
